<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h2"> {{ $t('operationEntityDataMapping.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="4" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button
            icon="plus"
            mod-icon-before
            :mod-disabled="editStore.isCreate(DeliveryProcessDefinitionEditType.OperationEntityDataMapping)"
            @click="editStore.openCreate(DeliveryProcessDefinitionEditType.OperationEntityDataMapping)"
            >{{ $t('general.add') }}</vl-button
          >
          <PbsActionButton
            class="operation-entity-data-actions"
            :label="$t('general.moreActions')"
            :items="actionButtonItems"
          >
          </PbsActionButton>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid :class="{ edit: editStore.isCreate(DeliveryProcessDefinitionEditType.OperationEntityDataMapping) }">
      <vl-column>
        <OperationEntityDataMappingCreate
          v-if="editStore.isCreate(DeliveryProcessDefinitionEditType.OperationEntityDataMapping)"
          :definition-id="definitionId"
          :parent-definition-id="parentDefinitionId"
        />
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="12" width-xs="12">
        <vl-properties-list>
          <vl-properties-label>{{ $t('operationEntityDataMapping.detail.sourceDefinition') }}</vl-properties-label>
          <vl-properties-data>
            <PbsEntityRouterLink type="deliveryProcessDefinition" :detail="props.detail.parent" />
          </vl-properties-data>
          <vl-properties-label>{{ $t('operationEntityDataMapping.detail.targetDefinition') }}</vl-properties-label>
          <vl-properties-data>
            <PbsEntityRouterLink type="deliveryProcessDefinition" :detail="props.detail" />
          </vl-properties-data>
        </vl-properties-list>
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="12" width-xs="12">
        <vl-accordion-list v-if="items && items.length">
          <vl-accordion-list-item v-for="(mapping, i) in items" :id="i" :key="i">
            <vl-accordion :id="i.toString()" :title="getLabel(mapping)">
              <vl-grid
                :class="{
                  edit: editStore.isEdit(DeliveryProcessDefinitionEditType.OperationEntityDataMapping, mapping.id)
                }"
              >
                <vl-column width="8">
                  <OperationEntityDataMappingEdit
                    v-if="editStore.isEdit(DeliveryProcessDefinitionEditType.OperationEntityDataMapping, mapping.id)"
                    ref="editForm"
                    :definition-id="definitionId"
                    :parent-definition-id="parentDefinitionId"
                    :detail="mapping"
                  />
                  <OperationEntityDataMappingRead v-else :detail="mapping" />
                </vl-column>
                <vl-column v-if="editStore.canEdit" width="4">
                  <vl-action-group
                    v-if="
                      mapping &&
                      !editStore.isEdit(DeliveryProcessDefinitionEditType.OperationEntityDataMapping, mapping.id)
                    "
                    mod-align-right
                    mod-space
                    between
                  >
                    <vl-button
                      icon="pencil"
                      mod-icon-before
                      mod-secondary
                      :mod-disabled="editStore.editing"
                      @click="
                        editStore.openEdit(DeliveryProcessDefinitionEditType.OperationEntityDataMapping, mapping.id)
                      "
                      >{{ $t('general.edit') }}</vl-button
                    >
                    <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal(mapping.id)">{{
                      $t('general.delete')
                    }}</vl-button>
                  </vl-action-group>
                  <vl-action-group v-else mod-align-right mod-space between>
                    <vl-button icon="save" x mod-icon-before :mod-loading="reloading" @click="save">{{
                      $t('general.save')
                    }}</vl-button>
                    <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                      {{ $t('general.cancel') }}
                    </vl-button>
                  </vl-action-group>
                </vl-column>
              </vl-grid>
            </vl-accordion>
          </vl-accordion-list-item>
        </vl-accordion-list>
        <template v-else>
          <vl-annotation>{{ $t('operationEntityDataMapping.detail.empty') }}</vl-annotation>
        </template>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import OperationEntityDataMappingRead from './OperationEntityDataMappingRead.vue';
import OperationEntityDataMappingCreate from './OperationEntityDataMappingCreate.vue';
import OperationEntityDataMappingEdit from './OperationEntityDataMappingEdit.vue';
import { DeliveryProcessDefinitionDetailDto, OperationEntityDataMappingDetailDto } from '@/api/backoffice/clients';
import { useOperationEntityDataMappingStore } from '@/modules/configuration/store/operation-entity-data-mapping/operation-entity-data-mapping.store';
import {
  DeliveryProcessDefinitionEditType,
  useDeliveryProcessDefinitionEditStore
} from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-edit.store';
import { computed, onMounted, ref, Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useModalStore } from '@/modules/core/store/modal.store';
import PbsEntityRouterLink from '@/components/PbsEntityRouterLink.vue';
import PbsActionButton, { PbsActionButtonItem } from '@/components/PbsActionButton.vue';

const { t } = useI18n();

const store = useOperationEntityDataMappingStore();
const editStore = useDeliveryProcessDefinitionEditStore();
const modalStore = useModalStore();

const editForm: Ref<typeof OperationEntityDataMappingEdit | (typeof OperationEntityDataMappingEdit)[]> = ref(null);

const route = useRoute();

const props = defineProps<{
  detail: DeliveryProcessDefinitionDetailDto;
}>();

const loading = computed((): boolean => {
  return store.loading;
});

const items = computed((): OperationEntityDataMappingDetailDto[] => {
  return store.byDefinition(definitionId.value);
});

const definitionId = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

const parentDefinitionId = computed((): number => {
  return props.detail.parent?.id;
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const actionButtonItems = computed((): PbsActionButtonItem[] => {
  return [
    {
      label: t('operationEntityDataMapping.actions.generate.label'),
      click: generateMapping
    },
    {
      label: t('general.delete'),
      click: () => showResetModal(),
      disabled: !editStore.canEdit || !items.value.length,
      ellipsis: true
    }
  ];
});

onMounted(async () => {
  await store.getByDefinition(definitionId.value);
});

const generateMapping = async () => {
  await store.generate(definitionId.value);
};

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showResetModal = async () => {
  modalStore.showDeleteModal({
    title: t('operationEntityDataMapping.detail.reset.title'),
    props: {
      confirm: () => store.reset(definitionId.value)
    }
  });
};

const showDeleteModal = (mappingId: number) => {
  modalStore.showDeleteModal({
    title: t('operationEntityDataMapping.detail.delete.title'),
    props: {
      confirm: () => store.remove(definitionId.value, mappingId)
    }
  });
};

const getLabel = (item: OperationEntityDataMappingDetailDto): string => {
  const unknown = '(?)';
  const from = `${item.fromEntity?.code ?? unknown}`;
  const to = `${item.toEntity?.code ?? unknown}`;

  return t('operationEntityDataMapping.detail.linkTo', { from, to });
};
</script>
<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
</style>
